// Virginia Tech: public college page (batch 2, #2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
// #3859 (2026-10-11), page vs KB: the headline class is now Fall 2025 (the
// 2025-26 column), the class the KB's cycle holds; the 2026-27 column (Fall
// 2026) is named in its note.
//  - Virginia Tech's Institutional Data application (University DataCommons,
//    Office of Analytics and Institutional Effectiveness), Admissions page,
//    with its default filters: Fall term, first-year (freshman) applicants.
//    Virginia Tech posts no Common Data Set file: its CDS page says the files
//    are "available via request", so cdsCycle is null. The latest year shown
//    is 2026-27 (Fall 2026 entry). The residency rows use the page's Tuition
//    filter (in-state or out-of-state tuition), and the by-college rows its
//    College Department Major view, read from the page's own data service.
//    The admissions office's first-year requirements page gives the same
//    Fall 2026 scale ("more than 58,000 applications").
//  - Virginia Tech Admissions pages: Dates and Deadlines for 2026-2027 (the
//    application that opened August 1, 2026, for Fall 2027 entry), Decision
//    Plans, First-Year Admissions Requirements (test-optional for students
//    entering through Fall 2028; the Fall 2028 course change). The deadlines
//    page gives no decision release dates.
//  - Virginia Tech Scholarships and Financial Aid, Cost of Attendance
//    2026-2027: the in-state and out-of-state on-campus budgets (PDFs dated
//    August 26, 2026), first-year figures, which include a computer budget.
//  - College Scorecard, school id 233921 (net price by family income). The
//    DEMO_KEY API call was over its rate limit, so the figures were read from
//    the same API response the Scorecard's school page loads.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    admissionsData: {
        label: 'Virginia Tech Institutional Data, Admissions',
        url: 'https://udc.vt.edu/irdata/data/students/admission/index',
        read: '2026-10-11',
    },
    deadlines: {
        label: 'Virginia Tech Admissions, Dates and Deadlines for 2026-2027',
        url: 'https://www.vt.edu/admissions/undergraduate/apply/dates-and-deadlines.html',
        read: '2026-10-01',
    },
    decisionPlans: {
        label: 'Virginia Tech Admissions, Undergraduate Decision Plans',
        url: 'https://www.vt.edu/admissions/undergraduate/apply/decision-plans.html',
        read: '2026-10-01',
    },
    requirements: {
        label: 'Virginia Tech Admissions, First-Year Admissions Requirements',
        url: 'https://www.vt.edu/admissions/undergraduate/apply/freshman-requirements.html',
        read: '2026-10-01',
    },
    majors: {
        label: 'Virginia Tech, Undergraduate Majors',
        url: 'https://www.vt.edu/academics/majors.html',
        read: '2026-10-01',
    },
    costs: {
        label: 'Virginia Tech Scholarships and Financial Aid, Cost of Attendance',
        url: 'https://finaid.vt.edu/undergraduate/coa.html',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?233921-Virginia-Polytechnic-Institute-and-State-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'virginia-tech',
    kbId: 'virginia_tech',
    name: 'Virginia Tech',
    shortName: 'Virginia Tech',
    aliases: ['VT', 'Virginia Polytechnic Institute and State University', 'Hokies'],
    location: 'Blacksburg, VA',
    control: 'Public',
    lastChecked: '2026-10-11',
    sources: SOURCES,
    // From the KB's sourced branding (Virginia Tech brand guidelines); decoration only.
    branding: {
        primary: '#861F41',
        secondary: '#E5751F',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/virginia_tech_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: null,
        applied: 57755,
        admitted: 31515,
        enrolled: 7133,
        source: 'admissionsData',
        where: 'Admissions, University view, Fall term, freshmen, 2025-26 (the 2026-27 column, the class that entered in fall 2026, shows 58,108 applied and 37,204 offered)',
        residency: [
            { group: 'In-state tuition', applied: 20900, admitted: 9893 },
            { group: 'Out-of-state tuition', applied: 36855, admitted: 21622 },
        ],
        residencySource: 'admissionsData',
        residencyWhere: 'Admissions, University view, by the Tuition filter, Fall term, freshmen, 2025-26',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        year: 'Fall 2026',
        published: true,
        note: 'Virginia Tech\'s Institutional Data application reports first-year applicants and offers by college for fall 2026. Virginia Tech reviews you by the major you request on your application, so the college here is the one that major belongs to. InterCollege is the label the data itself uses.',
        head: ['College', 'Applied', 'Offered', 'Rate'],
        rows: [
            { name: 'College of Agriculture and Life Sciences', applied: 4370, admitted: 2910, rate: 66.6 },
            { name: 'College of Architecture, Arts, and Design', applied: 3668, admitted: 1463, rate: 39.9 },
            { name: 'Pamplin College of Business', applied: 9333, admitted: 5178, rate: 55.5 },
            { name: 'College of Engineering', applied: 22972, admitted: 16055, rate: 69.9 },
            { name: 'College of Liberal Arts and Human Sciences', applied: 5709, admitted: 3374, rate: 59.1 },
            { name: 'College of Natural Resources and Environment', applied: 899, admitted: 582, rate: 64.7 },
            { name: 'College of Science', applied: 9497, admitted: 6689, rate: 70.4 },
            { name: 'College of Veterinary Medicine', applied: 666, admitted: 340, rate: 51.1 },
            { name: 'InterCollege', applied: 994, admitted: 613, rate: 61.7 },
        ],
        source: 'admissionsData',
        where: 'Admissions, College Department Major view, Fall term, freshmen, 2026-27',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Action', deadline: 'November 15', decision: null, binding: false },
            { plan: 'Regular Decision (space available)', deadline: 'January 15', decision: null, binding: false },
        ],
        note: 'The application opens August 1. Your STARS academic record (the course and grade record you enter yourself) is due December 1 for Early Action and January 24 for Regular Decision. The General Scholarship Application and the FAFSA are due January 22 for scholarship consideration. Admitted students accept by May 1. Virginia Tech does not publish decision release dates on its deadlines page.',
        source: 'deadlines',
        binding: 'Neither plan is binding: if you are admitted, you can weigh Virginia Tech\'s offer against offers from other schools. Virginia Tech calls Early Action its most common plan and encourages you to choose it if you apply by November 15.',
        bindingSource: 'decisionPlans',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Virginia Tech is test-optional for students entering through fall 2028. On the application you choose whether your SAT or ACT scores are reviewed; if you leave them out, Virginia Tech says it will not affect your decision. If you choose to include them, they are read. The Writing section of either test is not used.',
        source: 'requirements',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [
            { topic: 'Testing', line: 'Virginia Tech stays test-optional for fall 2028 entry: its policy covers students entering through fall 2028.', source: 'requirements' },
            { topic: 'Courses', line: 'From fall 2028, applicants need two units of a single foreign or classical language, or American Sign Language, among their 18 high school units. For fall 2027 a foreign language is highly recommended, not required.', source: 'requirements' },
        ],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [13938, 36428] },
            { item: 'Fees', amounts: [3150, 3754] },
            { item: 'Housing (on campus)', amounts: [10394, 10394] },
            { item: 'Food', amounts: [6826, 6826] },
            { item: 'Course materials', amounts: [1120, 1120] },
            { item: 'Personal', amounts: [3150, 3150] },
            { item: 'Transportation', amounts: [2478, 3014] },
            { item: 'Direct loan fees (only if you borrow)', amounts: [70, 70] },
            { item: 'Computer (first-year students)', amounts: [2150, 2150] },
        ],
        totals: [43276, 66906],
        note: 'First-year budget for students living on campus. The computer budget is $3,140 for engineering and architecture students. Some programs add a fee for the year: $750 for College of Agriculture and Biochemistry majors, $1,500 in the College of Architecture, Arts, and Design (Architecture, Industrial Design, Interior Design and Landscape Architecture), $2,000 for Building Construction and the College of Engineering, $2,550 in the Pamplin College of Business and $1,000 in the College of Science.',
        source: 'costs',
        netPrice: {
            average: 25808,
            byIncome: [
                { income: '$0 to $30,000', amount: 11378 },
                { income: '$30,001 to $48,000', amount: 9464 },
                { income: '$48,001 to $75,000', amount: 15085 },
                { income: '$75,001 to $110,000', amount: 22747 },
                { income: 'Over $110,000', amount: 35800 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Virginia Tech\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
